import { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  FACT_FIND_SECTION_ORDER_DOC,
  resolveFactFindSectionOrder,
} from '../constants/factFindSectionOrder';

const REQUIRED_FIELDS_DOC = 'system_config/required_fields';
const RETRY_DELAY_MS = 5000;

/**
 * One live Firestore subscription per document, shared by every mounted hook.
 * Value is `undefined` while loading, then the raw document data or `null`
 * when it does not exist / cannot be read.
 *
 * The app shell mounts this hook behind the login screen, and a snapshot
 * listener that errors is dead: one started before sign-in is refused by the
 * rules, which left every viewer on the built-in order (no global order, no
 * firm override) until a page reload. So it listens only while signed in,
 * starts afresh on each sign-in, and retries after an error while anyone is
 * still listening.
 */
function createSharedDoc(path) {
  const [collectionId, docId] = path.split('/');
  let value;
  let loaded = false;
  let unsubscribe = null;
  let unsubscribeAuth = null;
  let retryTimer = null;
  const listeners = new Set();

  const publish = (next) => {
    value = next;
    loaded = true;
    listeners.forEach((fn) => fn(value));
  };
  const stop = () => {
    clearTimeout(retryTimer);
    retryTimer = null;
    if (unsubscribe) unsubscribe();
    unsubscribe = null;
  };
  const start = () => {
    stop();
    if (!auth.currentUser) return;
    unsubscribe = onSnapshot(
      doc(db, collectionId, docId),
      (snap) => publish(snap.exists() ? snap.data() || null : null),
      () => {
        // Keep the last good value; callers fall back to the built-in order meanwhile.
        unsubscribe = null;
        publish(value || null);
        if (listeners.size === 0) return;
        retryTimer = setTimeout(() => {
          retryTimer = null;
          if (listeners.size > 0 && !unsubscribe) start();
        }, RETRY_DELAY_MS);
      },
    );
  };

  const ensure = () => {
    if (unsubscribeAuth) return;
    unsubscribeAuth = onAuthStateChanged(auth, (authUser) => {
      if (authUser) start();
      else stop();
    });
  };
  const release = () => {
    if (listeners.size > 0 || !unsubscribeAuth) return;
    unsubscribeAuth();
    unsubscribeAuth = null;
    stop();
    loaded = false;
    value = undefined;
  };

  return function useSharedDoc() {
    const [state, setState] = useState(() => (loaded ? value : undefined));
    useEffect(() => {
      const listener = (next) => setState(next);
      listeners.add(listener);
      ensure();
      if (loaded) setState(value);
      return () => {
        listeners.delete(listener);
        release();
      };
    }, []);
    return state;
  };
}

const useRequiredFieldsDoc = createSharedDoc(REQUIRED_FIELDS_DOC);
const useSectionOrderSettingsDoc = createSharedDoc(FACT_FIND_SECTION_ORDER_DOC);

/**
 * Effective Fact Find section ids for a viewer looking at a client. Marketing
 * users get their client's firm override when one exists; everyone else gets
 * the global order. Returns `null` until both documents have loaded so callers
 * can keep rendering nothing rather than flashing the wrong order. The array
 * is referentially stable while its contents are unchanged.
 *
 * @param {{ viewerRole: string|null, marketingTeamId: string|null }} args
 * @returns {string[]|null}
 */
export function useFactFindSectionOrder({ viewerRole, marketingTeamId }) {
  const requiredFieldsDoc = useRequiredFieldsDoc();
  const settingsDoc = useSectionOrderSettingsDoc();

  const key = useMemo(() => {
    if (requiredFieldsDoc === undefined || settingsDoc === undefined) return null;
    return resolveFactFindSectionOrder({
      globalOrder: requiredFieldsDoc?.sectionOrder,
      settingsDoc,
      viewerRole,
      marketingTeamId: marketingTeamId || null,
    }).join('|');
  }, [requiredFieldsDoc, settingsDoc, viewerRole, marketingTeamId]);

  return useMemo(() => (key === null ? null : key.split('|')), [key]);
}

export default useFactFindSectionOrder;
